import { GameObjectEvent } from 'GameObject/GameObject';
import { UIActions } from 'State/UI/UIActions';
import { PlayerActions } from 'State/Player/PlayerActions';
import Game from 'Game';
import { IFieldData } from 'State/World/IFieldData';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import Renderable from 'Component/Renderable';
import BaseUISystem from '../BaseUISystem';
import { FBTrackingEvent } from 'Utility/Tracking/FBTrackingEvent';
import { StartAnimation } from '../../Animation/AnimationHelper';
import { resetEventHandlerData, track } from 'Utility/Tracking/Track';
import { FTUXTrackingEventName } from 'Utility/Tracking/TrackingEvent';
import { IGameState } from 'State/Game/IGameState';
import { FieldStateHelper } from 'State/Player/Fields/FieldStateHelper';
import { PopupID } from 'State/UI/PopupID';
import { ThemeSettings } from 'Constants/Theme';
import Point from 'Utility/Point';
import { TransactionType } from 'Utility/Tracking/DataSet/Transaction';
import { AdContexts } from 'Utility/Tracking/DataSet/Ad';
import { GameActions } from 'State/Game/GameActions';
import { ContextType } from 'State/Game/ContextType';
import { AdsConfig } from 'Constants/Ads';
import { AdvertisementArea } from 'Utility/SDK/Advertisement';
import BaseMenu from 'System/UI/Menu/BaseMenu';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import TextComponent from 'Component/Render/Text';
import SpriteComponent from 'Component/Render/Sprite';
import { applyDefaultButtonAnimation } from 'System/UI/UIHelper';
import { FieldHarvestLayout } from 'Layouts/Harvest/FieldHarvestLayout';
import { RewardType } from 'Utility/Rewards/RewardType';
import { GLayout } from 'Utility/layout/LayoutHelper';
import { EventStateHelper } from 'State/Events/EventStateHelper';

export default class FieldHarvestHandler extends BaseMenu {
    private playAutoFlow: boolean;
    private layout: any;
    private worldFields: IFieldData[] = [];

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.HARVEST_MENU, uiSystem, false);
    }

    protected onCreate() {
        super.onCreate();
        const state = Game.store.getState();

        this.worldFields = WorldDataHelper.getAllFieldsData(state.playerState.maxWorld);
        this.worldFields.sort((a: IFieldData, b: IFieldData) => a.requiredLevel[0] - b.requiredLevel[0]);
    }

    public onShow(params?: any) {
        super.onShow(params);

        // TODO: What is this for?
        this.playAutoFlow = params.playAutoFlow ?? false;

        const state = Game.store.getState();

        // Create layout.
        this.layout = EventStateHelper.getEventLayout(state, PopupID.HARVEST_MENU) ?? new FieldHarvestLayout();
        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        this.element.addChild(this.layout.root);

        //
        //

        const playerMaxWorld = state.playerState.maxWorld;

        // Worlds 1 and 2 have different layouts active during each world (7 and 6 crops respectively).
        // All other worlds use a third layout (4 crops).
        const layoutIndex = Math.min(playerMaxWorld, 3);

        // Show previous unlocks slideshow after first world.
        if (layoutIndex > 1) {
            // Show slideshow section.
            this.layout.harvest_reward_frame_content.getComponent(Renderable).visible = true;
            this.layout.divider.getComponent(Renderable).visible = true;

            // Prepare previous world crop slideshow first texture.
            this.layout.harvest_reward_frame_content.getComponent(
                SpriteComponent
            ).texture = `collect_map_${playerMaxWorld - 1}`;

            // Start slideshow animation.
            StartAnimation(this.layout.harvest_reward_frame_content, 'HarvestFadeLoop', {
                maxWorld: playerMaxWorld,
                delay: 1500,
                duration: 1000,
            });
        }

        // Hide/show crop layouts based on max world.
        for (let i = 1; i <= 3; i++) {
            this.layout[`layout_${i}`].getComponent(Renderable).visible = Math.min(i, 3) === layoutIndex;
        }

        // Prepare field item icons.
        const playerCurrentLevel = Game.player.getHighestLevelCompleted(state);
        this.worldFields.forEach((field, cropIndex) => {
            this.prepareFieldItem(field, layoutIndex, cropIndex, playerCurrentLevel);
        });

        // Update harvest income text.
        const harvestIncome = FieldStateHelper.getHarvestIncome(state);
        this.layout.harvest_income_text.getComponent(TextComponent).text = String(harvestIncome);
        // TODO: Fix multiplier constant.
        this.layout.ad_harvest_income_text.getComponent(TextComponent).text = String(Math.floor(harvestIncome * 2));

        // Prepare buttons.
        const { collect_btn, ad_btn, exit_btn } = this.layout;
        [collect_btn, ad_btn, exit_btn].forEach(o => applyDefaultButtonAnimation(o));
        collect_btn.once(GameObjectEvent.SELECTED, this.onCollect, this);
        ad_btn.once(GameObjectEvent.SELECTED, this.onWatchAd, this);
        exit_btn.once(GameObjectEvent.SELECTED, this.onExit);
    }

    protected toggleInteractables(value: boolean): void {
        // handles interactables itself
    }

    protected onShown(params?: any): void {
        super.onShown(params);

        const state: IGameState = Game.store.getState();

        if (!state.playerState.FBEvents.includes(FBTrackingEvent.FTUE_15_HarvestPopupShow)) {
            Game.sdk.gameTracking(FBTrackingEvent.FTUE_15_HarvestPopupShow);
            PlayerActions.addFTUETrackingEvent(FBTrackingEvent.FTUE_15_HarvestPopupShow);

            track('stepId', 15);
            track('stepName', FBTrackingEvent.FTUE_15_HarvestPopupShow);
            resetEventHandlerData(FTUXTrackingEventName.ftux, Game.store.getState());
        }
    }

    public onDestroy(): void {
        super.onDestroy();
    }

    /**
     *
     * @param data
     * @param layoutIndex Passed to access unique ID in layout export. TODO: Support relative layout paths.
     * @param cropIndex
     * @param playerCurrentLevel
     * @private
     */
    private prepareFieldItem(data: IFieldData, layoutIndex: number, cropIndex: number, playerCurrentLevel: number) {
        let textureIndex: number = 0;
        let textVisible: boolean = true;
        let text: string = '';

        if (data.requiredLevel[1] <= playerCurrentLevel) {
            //activate _3
            textVisible = false;
            textureIndex = 2;
        } else if (data.requiredLevel[0] <= playerCurrentLevel) {
            //activate _2
            //text = data.requiredLevel[1].toString();
            textVisible = false;
            textureIndex = 1;
        } else {
            //activate text
            text = data.requiredLevel[0].toString();
            textureIndex = 0;
            textVisible = true;
        }

        const icon = this.layout[`crop_${layoutIndex}_${cropIndex + 1}`];
        icon.getComponent(SpriteComponent).texture = data.cropUnlockedTextures[textureIndex];

        const iconText = this.layout[`crop_text_${layoutIndex}_${cropIndex + 1}`];
        if (textVisible) {
            iconText.getComponent(TextComponent).text = Game.locale.getText('Level').replace('[x]', text);
        } else {
            iconText.getComponent(Renderable).visible = false;
        }
    }

    protected onCollect() {
        UIActions.hidePopup(PopupID.HARVEST_MENU);

        const state = Game.store.getState();
        if (FieldStateHelper.canHarvest(state)) {
            PlayerActions.harvest(this.playAutoFlow);
        }
    }

    private onWatchAd() {
        track('transactionType', TransactionType.AD);
        track('adContext', AdContexts.FREE_PLAY);

        UIActions.hidePopup(PopupID.HARVEST_MENU);

        const state: IGameState = Game.store.getState();
        if (FieldStateHelper.canHarvest(state)) {
            const harvestIncome = FieldStateHelper.getHarvestIncome(state);

            GameActions.setContext(ContextType.DISPLAY_ADVERTISEMENT, {
                placement: AdsConfig.getDoubleHarvest(Game.config),
                payload: {
                    g_gameArea: AdvertisementArea.DOUBLE_HARVEST,
                    reward: {
                        type: RewardType.COINS,
                        value: harvestIncome * ThemeSettings.AD_HARVEST_MULTIPLIER,
                    },
                    skipPopup: true,
                    playAutoFlow: this.playAutoFlow,
                },
            });
        }
    }

    private onExit() {
        UIActions.hidePopup(PopupID.HARVEST_MENU);
    }
}
